import { useSnackbar } from '@components/snackbar';
import { Input } from '@sketch-note/design-system/src';
import { Module } from '@sketch-note/design-system/src/components/cards/module.component';
import { Container } from '@sketch-note/design-system/src/components/container';
import { LessonLoader } from '@sketch-note/design-system/src/components/loaders';
import { StandardSnack } from '@sketch-note/design-system/src/components/snackbar';
import { isThirdElem } from '@sketch-note/design-system/src/shared/utils';
import { CloseIcon, SearchIcon } from '@sketchnote/icons';
import { Footer, FooterSecondary } from '@sketchnote/landing-page';
import {
  FullPageLoader,
  LoaderXs,
  MenuItemProps,
  MoreActionMenu,
} from '@sketchnote/shared-components';
import classNames from 'classnames';
import cn from 'classnames';
import { GetStaticProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/client';
import * as React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { API, endpoints } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { PageErrorBoundary } from 'src/components/error-boundary/page-error-boundary';
import { useSidebarStore } from 'src/components/sidebar/store';
import { withRoute } from 'src/hoc/public-page';
import { CatalogueLayout } from 'src/layouts/catalogue/catalogue.layout';
import { useAuth } from 'src/shared/hooks/use-auth';
import { RootState } from 'src/store/reducers';
import { SwiperSlide } from 'swiper/react';
import { useDebounce } from 'use-debounce';

import { CatalogCarousel } from '../components/Carousel';
import { FilterLabel } from '../components/FilterLabel';
const moduleTranslations = {
  bookmarked_flashcards: 'lesson flashcards & creation template',
  module_started: 'Module started',
  read_more: 'Read more',
  flashcard_count: 'learn cards',
  template_count: 'template tasks and',
};

const { useState, useEffect } = React;

const Catalogue = () => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const [isFilterOpened, setIsFilterOpened] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const router = useRouter();
  const [session, loading]: any = useSession();
  // eslint-disable-next-line prefer-const
  let [catalogData, setCatalogData] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentFilter, setCurrentFilter] = useState<any>({
    label: 'View all templates',
    value: 'view-all-lessons',
  });
  const [isLoading, setIsLoading] = useState(false);
  const authData = useAuth((s) => s.userDetails);
  const { learnAudio } = useSelector((state: RootState) => state.learn);
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const [openSnackbar] = useSnackbar();
  const fetchSidebar = useSidebarStore((s) => s.fetchSidebar);

  useEffect(() => {
    if (learnAudio) {
      learnAudio?.pause();
      learnAudio.currentTime = 0;
    }
  }, [learnAudio]);

  useEffect(() => {
    if (!loading && !session && orgId) {
      router.replace(`/catalog`);
    } else if (!loading && session && !orgId) {
      const memberOrg = authData?.memberInformation?.organisation?.organisationId;
      if (memberOrg) {
        router.replace(`/a/${memberOrg}/catalog`);
      }
    }
  }, [authData, loading]);

  const getCatalogData = async (
    pageNum = currentPage,
    isHideMyLessons = currentFilter.value === 'hide-my-lessons',
    search = debouncedSearchValue
  ) => {
    endpoints.catalog.start_page = pageNum;
    endpoints.catalog.limit_page = 3;
    endpoints.catalog.limit_card = 0;
    endpoints.catalog.start_card = 0;
    endpoints.catalog.hide_my_lessons = isHideMyLessons ? 1 : 0;
    endpoints.catalog.search = search;

    setIsLoading(true);
    const response = await API.get(endpoints.catalog.url);

    if (response?.data.search) {
      if (catalogData.length === 0) {
        catalogData.push(...response.data.search);
      } else {
        catalogData[0].items.push(...response.data.search[0].items);
      }
      if (response.data?.search[0].items?.length === 0) {
        setHasMore(false);
      }
    }
    if (response?.data.latest || response?.data.promoted || response?.data.lessons) {
      if (response?.data.latest) {
        catalogData.push({
          type: 'cover-card',
          items: [response.data.latest],
          category: {
            label: 'Fresh off the press',
          },
        });
      }

      if (response?.data.recommended) {
        catalogData.push({
          type: 'medium-card',
          items: [...response.data.recommended],
          category: {
            label: 'We recommend',
            slug: 'recommendations',
          },
        });
      }
      if (response?.data.promoted) {
        catalogData.push({
          type: 'promo-card',
          items: [...response.data.promoted],
          category: {
            label: 'Our top hits',
          },
        });
      }
      if (response.data.lessons) {
        catalogData.push(...response.data.lessons);
      }
    }
    if (response?.data?.lessons?.length === 0) {
      setHasMore(false);
    }
    setCatalogData([...catalogData]);
    setIsLoading(false);
  };

  useEffect(() => {
    catalogData = [];
    setCatalogData([]);
    setCurrentPage(0);
    setHasMore(true);
    getCatalogData(0, undefined, debouncedSearchValue);
    Analytics.track('Lesson Search', {
      'Search Word': debouncedSearchValue,
      'Search Source': 'Find New Lesson button',
    });
  }, [debouncedSearchValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleClear = () => {
    setSearchValue('');
  };

  const handleFilterToggle = (isOpen: boolean) => {
    setIsFilterOpened(isOpen);
  };

  const handleFetchMore = () => {
    getCatalogData(currentPage + 1);
    setCurrentPage(currentPage + 1);
  };
  const handleFilterSelection = (item: MenuItemProps) => {
    catalogData = [];
    setCatalogData([]);
    setCurrentFilter(item);
    setCurrentPage(0);
    setHasMore(true);
    getCatalogData(0, item.value === 'hide-my-lessons');
  };

  const handlePinLesson = async (slug: string, pin: boolean, name: string) => {
    const response = await API.post('/lesson/save', { mode: 'pin', slug });

    if (response.data.success) {
      fetchSidebar(mode);
      pin
        ? openSnackbar({
            text: `${name} pinned to sidebar`,
            snackType: 'standard',
            position: 'bottom-center',
            duration: 1500,
          })
        : openSnackbar({
            text: `${name} unpinned from sidebar`,
            snackType: 'standard',
            position: 'bottom-center',
          });
    }
  };

  return (
    <PageErrorBoundary wrapperClass="mt-32">
      <CatalogueLayout>
        <div className="pt-64 small:px-24 tablet:px-30 laptop:px-0">
          {(authData?.authentication === 'authorised' && !orgId) ||
          (authData?.authentication !== 'authorised' && orgId) ? (
            <FullPageLoader loading />
          ) : null}
          <Container>
            <h2 className="sm:w-8/12 mb-24">
              Choose from our popular modules to give your startup journey a boost!
            </h2>

            <div className="flex items-center justify-between mb-32">
              <Input
                placeholder="Search for a new template..."
                inputClassName="placeholder-gray-500 font-medium"
                field="search"
                leftIcon={<SearchIcon wrapperClass="align-text-bottom" />}
                wrapperClassName="py-14"
                value={searchValue}
                onChange={handleChange}
                rightIcon={searchValue ? <CloseIcon /> : null}
                onRightIconClick={handleClear}
                textSecClassName="w-6/12"
              />
              <div>
                <MoreActionMenu
                  menuItems={[
                    { label: 'View all templates', value: 'view-all-lessons' },
                    { label: 'Hide my templates', value: 'hide-my-lessons' },
                  ]}
                  icon={<FilterLabel label={currentFilter.label} isFilterOpened={isFilterOpened} />}
                  onOpenMenu={handleFilterToggle.bind(this, true)}
                  onCloseMenu={handleFilterToggle.bind(this, false)}
                  onClickMenuItem={handleFilterSelection}
                />
              </div>
            </div>
            {authData?.authentication === 'authorised' && (
              <div className="flex mb-82">
                <span className="mr-8 text-sm text-gray-600">Popular topics: </span>
                {[
                  { label: 'Product', slug: 'product-development' },
                  { label: 'Marketing', slug: 'sales-and-marketing' },
                  { label: 'Fundraising', slug: 'fundraising' },
                ].map((obj, index) => (
                  <Link
                    href={`/${mode}/${orgId}/catalog/category/${obj.slug}`}
                    key={`${obj.slug}-${index}`}
                  >
                    <a className="font-medium text-sm text-gray-600 mr-12">{obj.label}</a>
                  </Link>
                ))}
              </div>
            )}
          </Container>
          <Container className="flex flex-wrap">
            {catalogData?.length === 0 &&
              isLoading &&
              [1, 2, 3].map((data, index) => (
                <LessonLoader
                  key={data}
                  className={classNames({ 'mb-15': true, 'mr-15': !isThirdElem(index + 1) })}
                />
              ))}
          </Container>
          <div className="min-h-200">
            {catalogData?.length ? (
              <InfiniteScroll
                dataLength={
                  debouncedSearchValue
                    ? catalogData?.[0]?.items?.length | 0
                    : catalogData?.length || 0
                }
                next={handleFetchMore}
                hasMore={hasMore}
                loader={
                  <div className="h-50 overflow-hidden pb-90">
                    <LoaderXs className="mx-auto text-xss" />
                  </div>
                }
                endMessage={
                  <div className="flex items-center justify-center pb-64">
                    <StandardSnack text="That’s all folks! You’ve viewed the full catalog" />
                  </div>
                }
              >
                {catalogData?.map((cardData: any, index) => (
                  <React.Fragment key={`${cardData.category.label}-${index}`}>
                    {cardData.type !== 'promo-card' ? (
                      <Container
                        className={cn('mb-56 ', {
                          'small:hidden': cardData.type === 'cover-card',
                        })}
                      >
                        <div className="flex justify-between mb-24">
                          <h5 className="font-medium">{cardData.category.label}</h5>
                          {cardData.category?.slug ? (
                            <Link
                              href={`/${mode}/${orgId}/catalog/category/${cardData.category.slug}`}
                            >
                              <a className="text-blue-500 text-sm font-medium mr-24">View more</a>
                            </Link>
                          ) : null}
                        </div>

                        <div className="flex flex-wrap">
                          {cardData.type === 'cover-card' &&
                            cardData.items?.map((data: any, _index: number) => (
                              <Module
                                title={data.title}
                                key={`${data.title}-${_index}`}
                                handlePinLesson={handlePinLesson}
                                pinned={data?.pin}
                                tag={
                                  data.tag || { label: 'Label', color: '#456663', value: 'label' }
                                }
                                description={data.description}
                                stage={{ label: 'Stage', type: 'pre-revenue' }}
                                coverBgColor={data.banner_color}
                                size="xl"
                                slug={data?.slug}
                                flashCardCount={data.cards}
                                templateTaskCount={data.tasks}
                                className="w-full mb-16"
                                translations={moduleTranslations}
                                coverImg={data?.image?.url}
                                hasStarted={data?.hasStarted}
                                href={
                                  authData?.authentication === 'authorised'
                                    ? `/${mode}/${orgId}/lessons/${data?.slug}`
                                    : `/lessons/${data?.slug}`
                                }
                              />
                            ))}
                          {cardData.type === 'medium-card' &&
                            cardData.items?.map((data: any, _index: number) => (
                              <Module
                                hasStarted={data?.hasStarted}
                                key={`${data.title}-${_index}`}
                                size="md"
                                handlePinLesson={handlePinLesson}
                                pinned={data?.pin}
                                slug={data?.slug}
                                title={data?.title}
                                tag={
                                  data.tag || { label: 'Label', color: '#456663', value: 'label' }
                                }
                                description={data?.description}
                                stage={{ label: 'Stage', type: 'pre-revenue' }}
                                flashCardCount={data?.cards}
                                templateTaskCount={data?.tasks}
                                translations={moduleTranslations}
                                coverImg={data?.image?.url}
                                className={`mr-16 mb-16`}
                                href={
                                  authData?.authentication === 'authorised'
                                    ? `/${mode}/${orgId}/lessons/${data?.slug}`
                                    : `/lessons/${data?.slug}`
                                }
                              />
                            ))}
                        </div>
                      </Container>
                    ) : (
                      cardData.type === 'promo-card' && (
                        <div className="bg-primary-800 pt-78 pb-24 px-120 mb-56 w-full small:hidden">
                          <Container className="relative">
                            <h5 className="mb-22 text-white">{cardData.category.label}</h5>
                            <CatalogCarousel>
                              {cardData.items?.map((data: any, _index: number) => (
                                <SwiperSlide key={`${data.title}-${_index}`}>
                                  <Module
                                    hasStarted={data?.hasStarted}
                                    size="lg"
                                    title={data.title}
                                    slug={data?.slug}
                                    handlePinLesson={handlePinLesson}
                                    tag={
                                      data.tag || {
                                        label: 'Label',
                                        color: '#456663',
                                        value: 'label',
                                      }
                                    }
                                    pinned={data?.pin}
                                    description={data.description}
                                    stage={{ label: 'Stage', type: 'pre-revenue' }}
                                    flashCardCount={data.cards}
                                    templateTaskCount={data.tasks}
                                    translations={moduleTranslations}
                                    coverImg={data?.image?.url}
                                    className={`mr-16`}
                                    href={
                                      authData?.authentication === 'authorised'
                                        ? `/${mode}/${orgId}/lessons/${data?.slug}`
                                        : `/lessons/${data?.slug}`
                                    }
                                  />
                                </SwiperSlide>
                              ))}
                            </CatalogCarousel>
                          </Container>
                        </div>
                      )
                    )}
                  </React.Fragment>
                ))}
              </InfiniteScroll>
            ) : null}
          </div>
          {authData?.authentication !== 'authorised' && (
            <div>
              <FooterSecondary />
              <Footer />
            </div>
          )}
        </div>
      </CatalogueLayout>
    </PageErrorBoundary>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};

export default withRoute(Catalogue);
